import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import { CATEGORY_LABEL, LISTING_CATEGORIES, type ListingCategory } from "@/lib/categories";

export const Route = createFileRoute("/_authenticated/super-admin/partner-access")({
  head: () => ({
    meta: [
      { title: "Partner category access · Landoo Super Admin" },
      {
        name: "description",
        content: "Grant or revoke Sale, Rent, Lease and Parking access for partners.",
      },
    ],
  }),
  component: PartnerAccessPage,
});

interface Row {
  user_id: string;
  full_name: string | null;
  email: string | null;
  categories: ListingCategory[];
}

function PartnerAccessPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data: roles }, { data: cats }] = await Promise.all([
      supabase.from("user_roles").select("user_id").eq("role", "partner"),
      supabase.from("partner_categories").select("user_id,category,enabled"),
    ]);
    const ids = ((roles as { user_id: string }[]) ?? []).map((r) => r.user_id);
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id,full_name,email")
      .in("id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
    const catRows =
      (cats as { user_id: string; category: ListingCategory; enabled: boolean }[]) ?? [];
    setRows(
      ((profiles as { id: string; full_name: string | null; email: string | null }[]) ?? []).map(
        (p) => ({
          user_id: p.id,
          full_name: p.full_name,
          email: p.email,
          categories: catRows.filter((c) => c.user_id === p.id && c.enabled).map((c) => c.category),
        }),
      ),
    );
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const toggle = async (userId: string, category: ListingCategory, on: boolean) => {
    const { error } = await supabase
      .from("partner_categories")
      .upsert(
        { user_id: userId, category, enabled: on, source: "admin" },
        { onConflict: "user_id,category" },
      );
    if (error) return toast.error(error.message);
    toast.success(`${CATEGORY_LABEL[category]} ${on ? "enabled" : "disabled"}`);
    void load();
  };

  return (
    <DashboardShell title="Partner category access" role="Super Admin" items={SUPER_ADMIN_NAV}>
      {loading && <div className="text-sm text-muted-foreground">Loading partners…</div>}

      {!loading && rows.length === 0 && (
        <PlaceholderCard
          title="No partners yet"
          description="Once partners register, you can grant them Sale, Rent, Lease and Parking category access here."
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Partner</th>
                {LISTING_CATEGORIES.map((c) => (
                  <th key={c.value} className="px-3 py-2">
                    {CATEGORY_LABEL[c.value]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-brown-dark">{r.full_name ?? "Partner"}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  {LISTING_CATEGORIES.map((c) => {
                    const on = r.categories.includes(c.value);
                    return (
                      <td key={c.value} className="px-3 py-2">
                        <button
                          onClick={() => toggle(r.user_id, c.value, !on)}
                          className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                            on
                              ? "bg-brown text-primary-foreground"
                              : "bg-secondary text-brown-dark hover:bg-brand-soft"
                          }`}
                        >
                          {on ? "Enabled" : "Off"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
